import '@babylonjs/core/Meshes/thinInstanceMesh';
import type { TileMap } from '@archon3d/core';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import { tileShape } from './arenaMesh';

/** One tree: where in its tile it stands, how tall and wide, which way it is turned, how dark. */
export interface TreeProp {
  x: number;
  z: number;
  height: number;
  spread: number;
  yaw: number;
  shade: number;
}

/**
 * A number in [0, 1) from a tile's place and a salt: each tree is varied by where it stands, the
 * same on every screen and in every duel.
 */
export function tileHash(x: number, y: number, salt: number): number {
  let h =
    Math.imul(x + 1, 0x27d4eb2d) ^ Math.imul(y + 1, 0x165667b1) ^ Math.imul(salt + 1, 0x3c6ef372);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

/** How far a tree's leaves reach from its trunk at a spread of 1: the low cone of `treeGeometry`. */
export const LEAF_REACH = 0.62;
/** How far the high cone's leaves reach, at a spread of 1: the widest at a flier's height. */
export const HIGH_LEAF_REACH = 0.46;
/** The least room a trunk keeps from its tile's edge: a tree always stands on its own tile. */
export const TRUNK_ROOM = 0.15;
/** The widest a tree beside open ground grows, so that its leaves stay off that ground. */
export const BORDER_SPREAD = 1;

/**
 * Where a tree stands along one axis of its tile. Beside ground a fighter may stand on, with a
 * blocked side opposite (the arena's edge, a wall, the next tree), it stands at the back of its
 * tile, a few centimetres astray so that a row of them is not ruled straight, and no wider than
 * `BORDER_SPREAD`. A fighter at the edge of the ground then stands in front of the trees rather
 * than in their leaves: a flier, up among them, and one wider than its footing, had seemed to go
 * in behind the far border's trees (the owner, 2026-09-26). A tree with open ground on both sides,
 * or on neither, stays where it stood.
 */
function keepOff(at: number, cell: number, openBefore: boolean, openAfter: boolean): number {
  if (openBefore === openAfter) return at;
  // `at` lies within 0.12 of the tile's middle; a quarter of that is how far astray it stands.
  const astray = (at - cell - 0.38) * 0.25;
  return openAfter ? cell + TRUNK_ROOM + astray : cell + 1 - TRUNK_ROOM - astray;
}

/**
 * The trees of an arena: every tile whose family scatters (`autotile: "scatter"` in the tileset,
 * the fantasy set's trees), each a little off its tile's middle, turned, and between 85 % and all
 * of the tile's height. Never taller: the camera's fit and the duel's rules read that height.
 */
export function treesOf(map: Pick<TileMap, 'width' | 'height' | 'tile'>): TreeProp[] {
  const open = (x: number, y: number) => map.tile(x, y).walk !== 'blocked';
  const trees: TreeProp[] = [];
  for (let y = 0; y < map.height; y++)
    for (let x = 0; x < map.width; x++) {
      const shape = tileShape(map.tile(x, y));
      if (shape.kind !== 'prop') continue;
      const [west, east, south, north] = [
        open(x - 1, y),
        open(x + 1, y),
        open(x, y - 1),
        open(x, y + 1),
      ];
      const spread = 0.9 + 0.25 * tileHash(x, y, 4);
      const border = west !== east || south !== north;
      trees.push({
        x: keepOff(x + 0.5 + (tileHash(x, y, 1) - 0.5) * 0.24, x, west, east),
        z: keepOff(y + 0.5 + (tileHash(x, y, 2) - 0.5) * 0.24, y, south, north),
        height: shape.top * (0.85 + 0.15 * tileHash(x, y, 3)),
        spread: border ? Math.min(spread, BORDER_SPREAD) : spread,
        yaw: tileHash(x, y, 5) * Math.PI * 2,
        shade: 0.85 + 0.3 * tileHash(x, y, 6),
      });
    }
  return trees;
}

const BARK = new Color3(0.36, 0.25, 0.16);
const LEAVES_LOW = new Color3(0.15, 0.36, 0.13);
const LEAVES_HIGH = new Color3(0.2, 0.44, 0.16);

/**
 * One tree a metre wide and one tall, faceted: a six-sided trunk and two eight-sided cones of
 * leaves, 28 triangles. Its instances stretch it to their own height and spread.
 */
function treeGeometry(): VertexData {
  const positions: number[] = [];
  const normals: number[] = [];
  const colors: number[] = [];
  const indices: number[] = [];
  const triangle = (a: Vector3, b: Vector3, c: Vector3, color: Color3) => {
    // Babylon's front face is wound so that (c − a) × (b − a) points out of it.
    let normal = Vector3.Cross(c.subtract(a), b.subtract(a)).normalize();
    const middle = a
      .add(b)
      .add(c)
      .scale(1 / 3);
    if (normal.x * middle.x + normal.z * middle.z < 0) {
      [b, c] = [c, b];
      normal = normal.negate();
    }
    for (const p of [a, b, c]) {
      indices.push(positions.length / 3);
      positions.push(p.x, p.y, p.z);
      normals.push(normal.x, normal.y, normal.z);
      colors.push(color.r, color.g, color.b, 1);
    }
  };
  const ring = (sides: number, radius: number, y: number, turn: number) =>
    Array.from({ length: sides }, (_, i) => {
      const angle = ((i + turn) / sides) * Math.PI * 2;
      return new Vector3(Math.cos(angle) * radius, y, Math.sin(angle) * radius);
    });
  // The trunk: a tapering six-sided post up into the leaves.
  const foot = ring(6, 0.11, 0, 0);
  const neck = ring(6, 0.08, 0.42, 0);
  for (let i = 0; i < 6; i++) {
    const j = (i + 1) % 6;
    triangle(foot[i]!, foot[j]!, neck[j]!, BARK);
    triangle(foot[i]!, neck[j]!, neck[i]!, BARK);
  }
  // The leaves: a wide cone low, a narrower one over it, turned half a side against it.
  const cone = (radius: number, from: number, to: number, turn: number, color: Color3) => {
    const base = ring(8, radius, from, turn);
    const apex = new Vector3(0, to, 0);
    for (let i = 0; i < 8; i++) triangle(base[i]!, base[(i + 1) % 8]!, apex, color);
  };
  cone(LEAF_REACH, 0.25, 0.8, 0, LEAVES_LOW);
  cone(HIGH_LEAF_REACH, 0.52, 1, 0.5, LEAVES_HIGH);
  const data = new VertexData();
  data.positions = positions;
  data.normals = normals;
  data.colors = colors;
  data.indices = indices;
  return data;
}

/**
 * The arena's trees, drawn as trees (content:quality#2, x-aaa-gap#10) where the renderer drew
 * four-metre wooden blocks: all of them thin instances of one small mesh, one draw call however
 * many trees. The ground under them is drawn bare by `arenaMesh.ts`. `null` for an arena
 * without any.
 */
export function createTrees(
  scene: Scene,
  parent: TransformNode,
  map: Pick<TileMap, 'width' | 'height' | 'tile'>,
): { mesh: Mesh; dispose(): void } | null {
  const trees = treesOf(map);
  if (trees.length === 0) return null;
  const mesh = new Mesh('arenaTrees', scene);
  treeGeometry().applyToMesh(mesh, false);
  mesh.parent = parent;
  mesh.isPickable = false;
  // The fixed camera sees the whole arena; the one tree's own box would cull the rest.
  mesh.alwaysSelectAsActiveMesh = true;
  const material = new StandardMaterial('arenaTrees', scene);
  material.specularColor = Color3.Black();
  mesh.material = material;
  const matrices = new Float32Array(trees.length * 16);
  const colors = new Float32Array(trees.length * 4);
  const matrix = new Matrix();
  const turn = new Quaternion();
  trees.forEach((tree, i) => {
    Quaternion.RotationYawPitchRollToRef(tree.yaw, 0, 0, turn);
    Matrix.ComposeToRef(
      new Vector3(tree.spread, tree.height, tree.spread),
      turn,
      new Vector3(tree.x, 0, tree.z),
      matrix,
    );
    matrix.copyToArray(matrices, i * 16);
    colors.set([tree.shade, tree.shade, tree.shade, 1], i * 4);
  });
  mesh.thinInstanceSetBuffer('matrix', matrices, 16, true);
  mesh.thinInstanceSetBuffer('color', colors, 4, true);
  return {
    mesh,
    dispose() {
      material.dispose();
      mesh.dispose();
    },
  };
}
